import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Alert, Snackbar } from '@mui/material';
import './ParticipationPage.css';

const ParticipationPage = () => {
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [responses, setResponses] = useState({});
  const totalQuestions = 6;
  const [isComplete, setIsComplete] = useState(false);
  const [showQuestions, setShowQuestions] = useState(true);
  const [userId, setUserId] = useState("");
  const [response, setResponse] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userIdParam = params.get("u") || "";
    const responseParam = params.get("r") || "";
    const decodedUserId = userIdParam ? window.atob(userIdParam) : "";
    const decodedResponse = responseParam ? window.atob(responseParam) : "";
    setUserId(decodedUserId);
    setResponse(decodedResponse);
    if (decodedResponse.toLowerCase() === "yes") {
      setShowQuestions(true);
      // Call API to store response
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
      const url = `${baseUrl}engagement/participation?u=${encodeURIComponent(userIdParam)}&r=${encodeURIComponent(responseParam)}`;
      fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });
    } else if(decodedResponse.toLowerCase() === "no") {
      setShowQuestions(false);
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
      const url = `${baseUrl}engagement/participation?u=${encodeURIComponent(userIdParam)}&r=${encodeURIComponent(responseParam)}`;
      fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });
    }
  }, [location.search]);

  // Question data
  const questions = [
    {
      id: 1,
      text: "Did I do my best to set clear goals today?",
      subtitle: "Focus on your effort to define and clarify what you wanted to achieve"
    },
    {
      id: 2,
      text: "Did I do my best to make progress towards my goals today?",
      subtitle: "Rate your effort in taking concrete steps forward today"
    },
    {
      id: 3,
      text: "Did I do my best to find meaning today?",
      subtitle: "Consider your effort to connect with purpose and significance"
    },
    {
      id: 4,
      text: "Did I do my best to be happy today?",
      subtitle: "Rate your effort to maintain a positive mindset and outlook"
    },
    {
      id: 5,
      text: "Did I do my best to build positive relationships today?",
      subtitle: "Focus on your effort to connect, support, and collaborate with others"
    },
    {
      id: 6,
      text: "Did I do my best to be engaged today?",
      subtitle: "Rate your effort to be present, focused, and committed"
    }
  ];

  // All progress and indicator logic is now handled in render below

  const selectRating = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const nextQuestion = async () => {
    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      // On submit, call API
      await handleSubmit();
    }
  };


  const handleSubmit = async () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
    const params = new URLSearchParams(window.location.search);
    const d = params.get("d")
    const url = `${baseUrl}engagement/submit_question_ratings?u=${encodeURIComponent(window.btoa(userId))}&d=${encodeURIComponent(d || null)}`;
    // Build responses array
    const responsesArr = questions.map((question) => ({
      question: question.text,
      rating: responses[question.id] || null
    }));
    try {
      await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ responses: responsesArr })
      });
      setIsComplete(true);
      setSnackbarSeverity("success");
      setSnackbarMessage("Thank you for submitting your feedback!");
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to submit feedback. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const previousQuestion = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  // Removed completeSurvey, handled in nextQuestion

  const goToThankYou = () => {
    // In a real app, you would navigate to a thank you page
    console.log('Survey completed!', responses);
    // window.location.href = '#thank-you';
  };

  // Calculate progress: when on last question, progress is 100%
  const progress = currentQuestion === totalQuestions
    ? 100
    : ((currentQuestion - 1) / (totalQuestions - 1)) * 100;

  return (
    <>
      <div className="mg-participation-survey-container">
      <div className="mg-participation-header">
        <div className="mg-participation-header-content">
          <div className="mg-participation-date-info"><b style={{ color: '#ffffff'}}>{new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</b></div>
          <h1 style={{ color: '#ffffff'}}>Daily Leadership Check-in</h1>
          <p style={{ color: '#ffffff'}}><b>Take 2 minutes to reflect on your leadership efforts today. Remember: we're measuring effort, not results.</b></p>
        </div>
      </div>
      <div className="mg-participation-progress-section">
        <div className="mg-participation-progress-bar">
          <div className="mg-participation-progress-fill" style={{ width: `${progress}%` }}></div>
        </div>
        <div className="mg-participation-progress-text">
          <span>{isComplete ? 'Survey Complete!' : `Question ${currentQuestion} of ${totalQuestions}`}</span>
          <span className="mg-participation-streak-info">🔥</span>
        </div>
      </div>
      <div className="mg-participation-survey-content">
        {showQuestions ? (
          !isComplete ? (
            <>
              {questions.map(question => (
                currentQuestion === question.id && (
                  <div 
                    key={question.id}
                    className={`mg-participation-question-container active`}
                    data-question={question.id}
                  >
                    <div className="mg-participation-question-header">
                      <div className="mg-participation-question-number">Question {question.id} of {totalQuestions}</div>
                      <div className="mg-participation-question-text">{question.text}</div>
                      <div className="mg-participation-question-subtitle">{question.subtitle}</div>
                    </div>
                    {/* Show slider on mobile, original scale on desktop */}
                    <div className="mg-participation-rating-scale">
                      <div className="mg-participation-rating-scale-desktop">
                        {[...Array(10)].map((_, idx) => {
                          const value = idx + 1;
                          return (
                            <div 
                              key={value}
                              className={`mg-participation-scale-option${responses[question.id] === value ? ' selected' : ''}`}
                              onClick={() => selectRating(question.id, value)}
                            >
                              <div className="mg-participation-scale-number">{value}</div>
                              <div className="mg-participation-scale-label">
                                {value === 1 ? 'Lowest' : value === 10 ? 'Highest' : ''}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mg-participation-rating-scale-mobile">
                        <input
                          type="range"
                          min={1}
                          max={10}
                          value={responses[question.id] || 1}
                          onChange={e => selectRating(question.id, Number(e.target.value))}
                          className="mg-participation-slider"
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 4 }}>
                          <span>1</span>
                          <span>2</span>
                          <span>3</span>
                          <span>4</span>
                          <span>5</span>
                          <span>6</span>
                          <span>7</span>
                          <span>8</span>
                          <span>9</span>
                          <span>10</span>
                        </div>
                        <div style={{ textAlign: 'center', fontWeight: 600, marginTop: 20 }}>
                          Selected: {responses[question.id] || 1}
                        </div>
                      </div>
                    </div>
                    <div className="mg-participation-scale-endpoints">
                      <span>Less effort made</span>
                      <span>Maximum effort given</span>
                    </div>
                  </div>
                )
              ))}
              <div className="mg-participation-navigation">
                <button 
                  className="mg-participation-nav-button mg-participation-prev-button" 
                  onClick={previousQuestion}
                  disabled={currentQuestion === 1}
                >
                 <b> ← </b>
                </button>
                <div className="mg-participation-question-indicator">
                  {[...Array(totalQuestions)].map((_, index) => (
                    <div 
                      key={index}
                      className={`mg-participation-indicator-dot${index < currentQuestion - 1 ? ' completed' : ''}${index === currentQuestion - 1 ? ' current' : ''}`}
                    ></div>
                  ))}
                </div>
                <button 
                  className={currentQuestion === totalQuestions ? "mg-participation-nav-button mg-participation-submit-button" : "mg-participation-nav-button mg-participation-next-button"}
                  onClick={nextQuestion}
                  disabled={!responses[currentQuestion]}
                >
                 <b> {currentQuestion === totalQuestions ? 'Submit' : '→'}</b>
                </button>
              </div>
            </>
          ) : (
            <div className="mg-participation-completion-message show">
              <div className="mg-participation-completion-icon">✓</div>
              <h3>Survey Complete!</h3>
              <p>Thank you for taking time to reflect on your leadership today. Your responses have been saved.</p>
            </div>
          )
        ) : (
          <div className="mg-participation-completion-message show">
            <div className="mg-participation-completion-icon">✓</div>
            <h3>Thank you for your response!</h3>
            <p>Your participation has been recorded.</p>
          </div>
        )}
      </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ParticipationPage;
